import { Slider } from "@/components/ui/slider";
import { useEffect, useState, useRef } from "react";
import { quantizeImage, encodeIndexedPng } from "@/lib/png-compress";

const WARN_BYTES = 250 * 1024; // 250 KB — flagged as a heads-up, never enforced

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// PNG (and other formats with an alpha channel) must stay PNG — encoding to
// JPEG has no alpha channel, so transparent pixels get flattened to black.
function outputFormat(file: File): { mimeType: string; extension: string } {
  return file.type === "image/png"
    ? { mimeType: "image/png", extension: ".png" }
    : { mimeType: "image/jpeg", extension: ".jpg" };
}

async function getImageData(file: File): Promise<ImageData> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

// Maps the 1-100 quality slider onto imagequant's palette size (2-256
// colors) — lower quality means fewer colors, which is what actually shrinks
// a PNG (PNG's own quality argument is a no-op, it's always lossless).
function qualityToNumColors(quality: number): number {
  return Math.max(2, Math.round(2 + (quality / 100) * 254));
}

async function compressPng(file: File, quality: number): Promise<Blob> {
  const imageData = await getImageData(file);
  const quantized = await quantizeImage(
    imageData.data,
    imageData.width,
    imageData.height,
    qualityToNumColors(quality),
    1.0
  );
  const png = await encodeIndexedPng(quantized.data, quantized.width, quantized.height);
  return new Blob([png.buffer as ArrayBuffer], { type: "image/png" });
}

async function compressJpeg(file: File, quality: number): Promise<Blob | null> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality / 100));
}

// The slider's chosen quality is always what gets uploaded — this never
// second-guesses it or swaps in a different quality/size on its own. The only
// guard is against a re-encode that comes out *bigger* than what you started
// with, since that's never useful.
export async function compressImageFile(
  file: File,
  quality: number
): Promise<File> {
  const { mimeType, extension } = outputFormat(file);
  const blob =
    mimeType === "image/png"
      ? await compressPng(file, quality)
      : await compressJpeg(file, quality);
  if (!blob || blob.size >= file.size) {
    return file;
  }
  return new File([blob], file.name.replace(/\.[^.]+$/, extension), {
    type: mimeType,
  });
}

export function ImageCompressionSlider({
  file,
  quality,
  onChange,
}: {
  file: File;
  quality: number;
  onChange: (quality: number) => void;
}) {
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCompressedSize(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { mimeType } = outputFormat(file);
      const encode = mimeType === "image/png" ? compressPng(file, quality) : compressJpeg(file, quality);
      encode.then((blob) => setCompressedSize(blob?.size ?? file.size));
    }, 120);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [file, quality]);

  const isLarger = compressedSize !== null && compressedSize > file.size;
  const overWarnSize = compressedSize !== null && compressedSize > WARN_BYTES;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Quality: {quality}%</span>
        <span className="tabular-nums">
          {compressedSize === null ? (
            <span className="opacity-40">calculating…</span>
          ) : (
            <>
              <span className="opacity-60">{formatBytes(file.size)}</span>
              <span className="mx-1 opacity-40">→</span>
              <span
                className={
                  isLarger
                    ? "font-medium text-destructive"
                    : overWarnSize
                      ? "font-medium text-amber-500"
                      : "text-foreground font-medium"
                }
                title={
                  isLarger
                    ? "Larger than the original — the original will be uploaded instead"
                    : overWarnSize
                      ? "Over 250 KB — lower the quality if you want it smaller"
                      : undefined
                }
              >
                {formatBytes(compressedSize)}
              </span>
            </>
          )}
        </span>
      </div>
      <Slider
        min={1}
        max={100}
        step={1}
        value={[quality]}
        onValueChange={([v]) => onChange(v)}
      />
    </div>
  );
}
